import React, { useState, useRef } from 'react';
import { Box, Container, Input, IconButton, Alert, Snackbar } from '@mui/material';

import AdsenseAd from './AdsenseAd';
import CameraView from './CameraView';
import CanvasEditor from './CanvasEditor';
import SilhouetteCarousel from './SilhouetteCarousel';
import ControlsOverlay from './ControlsOverlay';
import useCamera from '../hooks/useCamera';
import useCanvas from '../hooks/useCanvas';
import useSilhouette from '../hooks/useSilhouette';
import ShareSheet from './ShareSheet';

function AppPage() {
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [captureReview, setCaptureReview] = useState(false);
  const fileInputRef = useRef(null);

  const {
    silhouetteImage, loadedSilhouette,
    silhouetteOffsetX, silhouetteOffsetY, silhouetteScale, silhouetteOpacity,
    transformHistory, transformFuture, dispatchTransform, handleSilhouetteSelect, resetSilhouette,
  } = useSilhouette();

  const { videoRef, isCameraActive, closeCamera } = useCamera();

  const {
    canvasRef, isDragging,
    handleCanvasMouseDown, handleCanvasMouseMove, handleCanvasMouseUp, handleCanvasMouseLeave,
    handleCanvasTouchStart, handleCanvasTouchMove, handleCanvasTouchEnd, handleCanvasAreaClick,
  } = useCanvas({
    backgroundImage, loadedSilhouette, silhouetteScale, silhouetteOpacity,
    silhouetteOffsetX, silhouetteOffsetY, dispatchTransform, fileInputRef, isCameraActive,
  });

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setValidationError('File too large. Please use an image under 10MB.');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
        setValidationError('Unsupported format. Please use JPG, PNG, WebP, or GIF.');
        return;
      }
      setValidationError(null);
      setIsProcessing(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        setBackgroundImage(e.target.result);
        resetSilhouette();
        dispatchTransform({ type: 'RESET_TRANSFORM' });
        if (isCameraActive) closeCamera();
        setIsProcessing(false);
      };
      reader.onerror = () => {
        setValidationError('Failed to read file. Please try again.');
        setIsProcessing(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      setIsProcessing(true);
      try {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.translate(video.videoWidth, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        setBackgroundImage(dataUrl);
        resetSilhouette();
        dispatchTransform({ type: 'RESET_TRANSFORM' });
        setCaptureReview(true);
        setIsProcessing(false);
      } catch (error) {
        setIsProcessing(false);
      }
    }
  };

  const handleConfirmCapture = () => {
    setCaptureReview(false);
    closeCamera();
  };

  const handleRetakePhoto = () => {
    setCaptureReview(false);
    setBackgroundImage(null);
    resetSilhouette();
    dispatchTransform({ type: 'RESET_TRANSFORM' });
  };

  const handleDownloadImage = () => {
    const canvas = canvasRef.current;
    if (canvas && backgroundImage) {
      try {
        const imageURL = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = imageURL;
        link.download = 'nutscaped-image.png';
        document.body.appendChild(link);
        link.click();
        setDownloadSuccess(true);
        document.body.removeChild(link);
      } catch (error) {
        setDownloadSuccess(false);
      }
    }
  };

  const handleGetCanvasBlob = () => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (canvas && backgroundImage) {
        try {
          canvas.toBlob((blob) => resolve(blob), 'image/png');
        } catch (e) {
          resolve(null);
        }
      } else {
        resolve(null);
      }
    });
  };

  const [currentSilhouetteStartIndex, setCurrentSilhouetteStartIndex] = useState(0);
  const silhouettesPerPage = 3;
  const silhouettes = [
    { name: 'Silhouette 1', src: '/img/silhouette1.png' },
    { name: 'Silhouette 2', src: '/img/silhouette2.png' },
    { name: 'Silhouette 3', src: '/img/silhouette3.png' },
    { name: 'Hanging', src: '/img/silhouette4.svg' },
    { name: 'Handstand', src: '/img/silhouette5.svg' },
    { name: 'Bird', src: '/img/silhouette6.svg' },
    { name: 'Balloon', src: '/img/silhouette7.svg' },
    { name: 'Jump', src: '/img/silhouette8.svg' },
    { name: 'Plane', src: '/img/silhouette9.svg' },
  ];
  const handleNextSilhouettes = () => setCurrentSilhouetteStartIndex(prevIndex => Math.min(prevIndex + silhouettesPerPage, silhouettes.length - silhouettesPerPage));
  const handlePrevSilhouettes = () => setCurrentSilhouetteStartIndex(prevIndex => Math.max(prevIndex - silhouettesPerPage, 0));

  return (
    <Container maxWidth="lg" sx={{ py: 3, textAlign: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #0D0B14 0%, #1A1028 50%, #0D0B14 100%)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, mb: (isCameraActive || backgroundImage) ? 0 : 3, flexShrink: 0, ...(isCameraActive && { flexGrow: 1, display: 'flex', flexDirection: 'column', p: 0, mb: 0 }) }}>
        <Input type="file" accept="image/*" onChange={handleImageUpload} inputRef={fileInputRef} sx={{ display: 'none' }} />
        {isCameraActive && (
          <CameraView
            videoRef={videoRef}
            fileInputRef={fileInputRef}
            isCameraActive={isCameraActive}
            onCapture={handleCapturePhoto}
            onUploadClick={() => fileInputRef.current && fileInputRef.current.click()}
            captureReview={captureReview}
            backgroundImage={backgroundImage}
            onConfirm={handleConfirmCapture}
            onRetake={handleRetakePhoto}
            silhouetteImage={silhouetteImage}
            silhouettes={silhouettes}
            currentSilhouetteStartIndex={currentSilhouetteStartIndex}
            silhouettesPerPage={silhouettesPerPage}
            onSelectSilhouette={handleSilhouetteSelect}
            onNextSilhouettes={handleNextSilhouettes}
            onPrevSilhouettes={handlePrevSilhouettes}
            silhouetteScale={silhouetteScale}
            silhouetteOpacity={silhouetteOpacity}
            transformHistory={transformHistory}
            transformFuture={transformFuture}
            onUndo={() => dispatchTransform({ type: 'UNDO' })}
            onRedo={() => dispatchTransform({ type: 'REDO' })}
            onScaleChange={(e, newValue) => dispatchTransform({ type: 'SET_TRANSFORM', payload: { scale: newValue } })}
            onOpacityChange={(e, newValue) => dispatchTransform({ type: 'SET_TRANSFORM', payload: { opacity: newValue } })}
          />
        )}
      </Box>
      <Box
        sx={backgroundImage && !isCameraActive ? {
          flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', backgroundColor: 'black', position: 'relative', p: 0,
        } : {
          mt: backgroundImage ? 2 : 0, p: 0.5, border: '1px dashed', borderColor: 'divider',
          borderRadius: 1, display: 'inline-block', lineHeight: 0,
          cursor: !backgroundImage && !isCameraActive ? 'pointer' : 'default', position: 'relative',
        }}
        onClick={!backgroundImage && !isCameraActive ? handleCanvasAreaClick : undefined}
      >
        <CanvasEditor
          canvasRef={canvasRef}
          backgroundImage={backgroundImage}
          isCameraActive={isCameraActive}
          isDragging={isDragging}
          loadedSilhouette={loadedSilhouette}
          isProcessing={isProcessing}
          validationError={validationError}
          onValidationErrorClose={() => setValidationError(null)}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseLeave}
          onTouchStart={handleCanvasTouchStart}
          onTouchMove={handleCanvasTouchMove}
          onTouchEnd={handleCanvasTouchEnd}
        />
        {!isCameraActive && backgroundImage && (
          <ControlsOverlay
            silhouetteScale={silhouetteScale}
            silhouetteOpacity={silhouetteOpacity}
            transformHistory={transformHistory}
            transformFuture={transformFuture}
            onUndo={() => dispatchTransform({ type: 'UNDO' })}
            onRedo={() => dispatchTransform({ type: 'REDO' })}
            onScaleChange={(e, newValue) => dispatchTransform({ type: 'SET_TRANSFORM', payload: { scale: newValue } })}
            onOpacityChange={(e, newValue) => dispatchTransform({ type: 'SET_TRANSFORM', payload: { opacity: newValue } })}
          />
        )}
        {!isCameraActive && backgroundImage && (
          <SilhouetteCarousel
            silhouettes={silhouettes}
            silhouetteImage={silhouetteImage}
            currentSilhouetteStartIndex={currentSilhouetteStartIndex}
            silhouettesPerPage={silhouettesPerPage}
            onSelect={handleSilhouetteSelect}
            onPrev={handlePrevSilhouettes}
            onNext={handleNextSilhouettes}
          />
        )}
        {!isCameraActive && backgroundImage && (
          <IconButton
            aria-label="download image"
            onClick={handleDownloadImage}
            disabled={isProcessing || !backgroundImage}
            sx={{ position: 'absolute', top: '10px', left: '10px', zIndex: 20, backgroundColor: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.15)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }, p: '8px' }}
          >
            <img src="/img/Save.png" alt="Save" style={{ width: 24, height: 24 }} />
          </IconButton>
        )}
        {!isCameraActive && backgroundImage && (
          <Box sx={{ position: 'absolute', top: '10px', right: '10px', zIndex: 20 }}>
            <ShareSheet onShare={handleGetCanvasBlob} disabled={isProcessing || !backgroundImage} />
          </Box>
        )}
      </Box>
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <AdsenseAd publisherId="ca-pub-2352940492649564" adSlot="7802069983" />
      </Box>
      <Snackbar open={downloadSuccess} autoHideDuration={3000} onClose={() => setDownloadSuccess(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setDownloadSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Image downloaded! 🎉
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AppPage;
